import type {
  ConsentData,
  ScriptBlockerRule,
  ConsentManagerConfig,
} from '@/core/types';

export class ScriptBlocker {
  private static readonly BLOCKED_ATTRIBUTE = 'data-cmp-blocked';
  private static readonly ORIGINAL_SRC_ATTRIBUTE = 'data-cmp-original-src';
  private static readonly ORIGINAL_TYPE_ATTRIBUTE = 'data-cmp-original-type';

  private rules: ScriptBlockerRule[];
  private observer: MutationObserver | null = null;
  private blockedScripts: Set<HTMLScriptElement> = new Set();
  private isBlocking = false;

  constructor(config: ConsentManagerConfig) {
    this.rules = this.getDefaultRules();

    // Only initialize blocking if script interceptor is enabled
    if (config.enableScriptInterceptor === true) {
      this.isBlocking = true;
      this.initializeBlocking();
    }
  }

  public startBlocking(): void {
    if (this.isBlocking) return;

    this.isBlocking = true;
    this.blockExistingScripts();
    this.startObserving();
  }

  public stopBlocking(): void {
    if (!this.isBlocking) return;

    this.isBlocking = false;
    this.stopObserving();
  }

  public updateConsent(consent: ConsentData): void {
    this.unblockScriptsForConsent(consent);
  }

  public addRule(rule: ScriptBlockerRule): void {
    this.rules.push(rule);
  }

  public removeRule(selector: string): void {
    this.rules = this.rules.filter((rule) => rule.selector !== selector);
  }

  public getBlockedScripts(): HTMLScriptElement[] {
    return Array.from(this.blockedScripts);
  }

  private initializeBlocking(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.blockExistingScripts();
        this.startObserving();
      });
    } else {
      this.blockExistingScripts();
      this.startObserving();
    }
  }

  private blockExistingScripts(): void {
    const scripts = document.querySelectorAll('script[src]');
    scripts.forEach((script) => {
      if (script instanceof HTMLScriptElement) {
        this.processScript(script);
      }
    });
  }

  private startObserving(): void {
    if (this.observer) return;

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;

              if (element.tagName === 'SCRIPT') {
                this.processScript(element as HTMLScriptElement);
              }

              const scripts = element.querySelectorAll('script[src]');
              scripts.forEach((script) => {
                if (script instanceof HTMLScriptElement) {
                  this.processScript(script);
                }
              });
            }
          });
        }

        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'src' &&
          mutation.target instanceof HTMLScriptElement
        ) {
          this.processScript(mutation.target);
        }
      });
    });

    this.observer.observe(document, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'type'],
    });
  }

  private stopObserving(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private processScript(script: HTMLScriptElement): void {
    if (!this.isBlocking) return;
    if (this.isScriptBlocked(script)) return;
    if (this.isConsentManagerScript(script)) return;

    const matchingRule = this.findMatchingRule(script);
    if (matchingRule) {
      this.blockScript(script, matchingRule);
    }
  }

  private findMatchingRule(
    script: HTMLScriptElement
  ): ScriptBlockerRule | null {
    const src = script.src || '';
    const type = script.type || '';
    const className = script.className || '';
    const id = script.id || '';

    return (
      this.rules.find((rule) => {
        if (rule.selector.startsWith('src:')) {
          const pattern = rule.selector.substring(4);
          return src.includes(pattern);
        }

        if (rule.selector.startsWith('type:')) {
          const pattern = rule.selector.substring(5);
          return type.includes(pattern);
        }

        if (rule.selector.startsWith('class:')) {
          const pattern = rule.selector.substring(6);
          return className.includes(pattern);
        }

        if (rule.selector.startsWith('id:')) {
          const pattern = rule.selector.substring(3);
          return id === pattern;
        }

        try {
          return script.matches(rule.selector);
        } catch {
          return false;
        }
      }) || null
    );
  }

  private blockScript(
    script: HTMLScriptElement,
    rule: ScriptBlockerRule
  ): void {
    if (script.src) {
      script.setAttribute(ScriptBlocker.ORIGINAL_SRC_ATTRIBUTE, script.src);
      script.removeAttribute('src');
    }

    if (script.type) {
      script.setAttribute(ScriptBlocker.ORIGINAL_TYPE_ATTRIBUTE, script.type);
    }

    script.type = 'javascript/blocked';
    script.setAttribute(ScriptBlocker.BLOCKED_ATTRIBUTE, rule.category);

    this.blockedScripts.add(script);

    console.log(
      `🚫 ConsentManager v1.0.2: Blocked ${rule.category} script:`,
      script.src || script.textContent?.substring(0, 50)
    );
  }

  private unblockScriptsForConsent(consent: ConsentData): void {
    const scriptsToUnblock: HTMLScriptElement[] = [];

    this.blockedScripts.forEach((script) => {
      const category = script.getAttribute(
        ScriptBlocker.BLOCKED_ATTRIBUTE
      ) as keyof ConsentData;

      if (category && consent[category] === true) {
        scriptsToUnblock.push(script);
      }
    });

    scriptsToUnblock.forEach((script) => {
      this.unblockScript(script);
    });
  }

  private unblockScript(script: HTMLScriptElement): void {
    const originalSrc = script.getAttribute(
      ScriptBlocker.ORIGINAL_SRC_ATTRIBUTE
    );
    const originalType = script.getAttribute(
      ScriptBlocker.ORIGINAL_TYPE_ATTRIBUTE
    );

    script.removeAttribute(ScriptBlocker.BLOCKED_ATTRIBUTE);
    script.removeAttribute(ScriptBlocker.ORIGINAL_SRC_ATTRIBUTE);
    script.removeAttribute(ScriptBlocker.ORIGINAL_TYPE_ATTRIBUTE);

    if (originalType) {
      script.type = originalType;
    } else {
      script.removeAttribute('type');
    }

    this.blockedScripts.delete(script);

    if (originalSrc) {
      const newScript = document.createElement('script');
      newScript.src = originalSrc;

      if (originalType) {
        newScript.type = originalType;
      }

      Array.from(script.attributes).forEach((attr) => {
        if (
          attr.name !== 'src' &&
          attr.name !== 'type' &&
          !attr.name.startsWith('data-cmp-')
        ) {
          newScript.setAttribute(attr.name, attr.value);
        }
      });

      script.parentNode?.replaceChild(newScript, script);

      console.log('✅ ConsentManager v1.0.2: Unblocked script:', originalSrc);
    }
  }

  private isScriptBlocked(script: HTMLScriptElement): boolean {
    return script.hasAttribute(ScriptBlocker.BLOCKED_ATTRIBUTE);
  }

  private isConsentManagerScript(script: HTMLScriptElement): boolean {
    const src = script.src || '';
    return (
      src.includes('consent') &&
      (src.includes('cmp') || src.includes('consent-manager'))
    );
  }

  private getDefaultRules(): ScriptBlockerRule[] {
    return [
      {
        selector: 'src:googletagmanager.com/gtag',
        category: 'essential', // Changed from analytics - GTM should always load
      },
      {
        selector: 'src:google-analytics.com/analytics.js',
        category: 'analytics',
      },
      {
        selector: 'src:googletagmanager.com/gtm.js',
        category: 'essential', // Changed from analytics - GTM container should always load
      },
      {
        selector: 'src:connect.facebook.net',
        category: 'advertising',
      },
      {
        selector: 'src:platform.twitter.com',
        category: 'advertising',
      },
      {
        selector: 'src:snap.licdn.com',
        category: 'advertising',
      },
      {
        selector: 'src:bat.bing.com',
        category: 'advertising',
      },
      {
        selector: 'src:pinterest.com/v3/conversion',
        category: 'advertising',
      },
      {
        selector: 'src:tiktok.com/i18n/pixel',
        category: 'advertising',
      },
      {
        selector: 'src:hotjar.com',
        category: 'analytics',
      },
      {
        selector: 'src:fullstory.com',
        category: 'analytics',
      },
      {
        selector: 'src:mixpanel.com',
        category: 'analytics',
      },
      {
        selector: 'src:segment.com',
        category: 'analytics',
      },
      {
        selector: 'src:youtube.com/iframe_api',
        category: 'functional',
      },
      {
        selector: 'src:maps.googleapis.com',
        category: 'functional',
      },
      {
        selector: 'src:recaptcha.google.com',
        category: 'essential',
      },
      {
        selector: 'src:stripe.com/v3',
        category: 'essential',
      },
      {
        selector: 'src:paypal.com',
        category: 'essential',
      },
      {
        selector: 'src:intercom.io',
        category: 'functional',
      },
      {
        selector: 'src:zendesk.com',
        category: 'functional',
      },
      {
        selector: 'src:drift.com',
        category: 'functional',
      },
      {
        selector: 'src:hubspot.com',
        category: 'functional',
      },
      {
        selector: 'src:salesforce.com',
        category: 'functional',
      },
    ];
  }

  public destroy(): void {
    this.stopObserving();
    this.blockedScripts.clear();
  }
}
